import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactSelect from 'react-select';
import { Label } from 'reactstrap';

import { messages } from '../../../_shared/i18n/messages';

import DatePicker from '../DatePicker';
import TimePicker from '../TimePicker';
import TimezonePicker from '../TimezonePicker';

import './DateTimeRangePicker.scss';

const propTypes = {

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The input value.
	 *
	 * For a range of datetimes, it should be an array of two MomentJS objects.
	 */
	value: PropTypes.arrayOf(PropTypes.instanceOf(moment)),

	/**
	 * The default timezone to use.
	 */
	defaultTimezone: PropTypes.string.isRequired,

	/**
	 * True, if the input value is invalid.
	 */
	isInvalid: PropTypes.bool.isRequired,

	/**
	 * True, if the input value is required.
	 */
	isRequired: PropTypes.bool.isRequired,

	/**
	 * Callback to handle changes to the input value.
	 */
	onChange: PropTypes.func.isRequired,

};

const defaultProps = {
	isInvalid: false,
	isRequired: false,
};

const DateTimeRangePicker = (props) => {

	const {
		name,
		value,
		defaultTimezone,
		isInvalid,
		isRequired,
		onChange,
	} = props;

	const intl = useIntl();

	const startDateLabel = `${name}_start_date`;
	const startTimeLabel = `${name}_start_time`;
	const endDateLabel = `${name}_end_date`;
	const endTimeLabel = `${name}_end_time`;
	const timezoneLabel = `${name}_timezone`;

	const formValues = useMemo(() => {

		let startDate, startTime = '';

		let endDate, endTime = '';

		let timezone = defaultTimezone;

		if (value && Array.isArray(value)) {

			const formattedStartValue =
				value[0] &&
				value[0].format('YYYY-MM-DD h:mma');

			const formattedEndValue =
				value[1] &&
				value[1].format('YYYY-MM-DD h:mma');

			let timezone1, timezone2;

			if (formattedStartValue) {

				startDate = formattedStartValue.split(' ')[0];
				startTime = formattedStartValue.split(' ')[1];
				timezone1 = value[0]._z && value[0]._z.name;

			}

			if (formattedEndValue) {

				endDate = formattedEndValue.split(' ')[0];
				endTime = formattedEndValue.split(' ')[1];
				timezone2 = value[1]._z && value[1]._z.name;

			}

			if (timezone1 &&
				timezone2 &&
				timezone1 !== timezone2) {
				console.error(`The timezones do not match for the "${name}" field`);
			}

			timezone =
				timezone1 ||
				timezone2 ||
				defaultTimezone;

		}

		return {
			startDate,
			startTime,
			endDate,
			endTime,
			timezone,
		};

	}, [name, value, defaultTimezone]);

	const handleStartChange = (tempName, tempValue) => {

		let newValue;

		// Check if a valid date value was selected
		if (tempValue) {

			const newTimezone = formValues.timezone;

			let newDate, newTime;

			if (tempName === startDateLabel) {

				newDate = tempValue.format('YYYY-MM-DD');

				// Default to current time
				newTime =
					formValues.startTime ||
					moment().tz(newTimezone).format('h:mma');

			} else if (tempName === startTimeLabel) {

				// Default to current date
				newDate =
					formValues.startDate ||
					moment().tz(newTimezone).format('YYYY-MM-DD');

				newTime = tempValue;

			}

			newValue = moment.tz(`${newDate} ${newTime}`, 'YYYY-MM-DD h:mma', newTimezone);

		// If a value is required, default to the current date
		} else if (isRequired) {

			const newTimezone = formValues.timezone;

			let newDate, newTime;

			if (tempName === startDateLabel) {

				// Default to current date
				newDate = moment().tz(newTimezone).format('YYYY-MM-DD');

				// Default to current time
				newTime =
					formValues.startTime ||
					moment().tz(newTimezone).format('h:mma');

				newValue = moment.tz(`${newDate} ${newTime}`, 'YYYY-MM-DD h:mma', newTimezone);

			}

		// Otherwise, reset the value
		} else {

			newValue = null;

		}

		let result;

		if (value && Array.isArray(value)) {

			result = [...value];

			result[0] = newValue;

			// Check if the end datetime needs to be automatically updated
			if (result[0] &&
				(!result[1] || result[1].isBefore(result[0]))
			) {
				result[1] = result[0];
			}

		} else {

			result = [newValue, newValue];

		}

		onChange(result);

	};

	const handleEndChange = (tempName, tempValue) => {

		let newValue;

		// Check if a valid date value was selected
		if (tempValue) {

			const newTimezone = formValues.timezone;

			let newDate, newTime;

			if (tempName === endDateLabel) {

				newDate = tempValue.format('YYYY-MM-DD');

				// Default to current time
				newTime =
					formValues.endTime ||
					moment().tz(newTimezone).format('h:mma');

			} else if (tempName === endTimeLabel) {

				// Default to current date
				newDate =
					formValues.endDate ||
					moment().tz(newTimezone).format('YYYY-MM-DD');

				newTime = tempValue;

			}

			newValue = moment.tz(`${newDate} ${newTime}`, 'YYYY-MM-DD h:mma', newTimezone);

		// If a value is required, default to the current date
		} else if (isRequired) {

			const newTimezone = formValues.timezone;

			let newDate, newTime;

			if (tempName === endDateLabel) {

				// Default to current date
				newDate = moment().tz(newTimezone).format('YYYY-MM-DD');

				// Default to current time
				newTime =
					formValues.endTime ||
					moment().tz(newTimezone).format('h:mma');

				newValue = moment.tz(`${newDate} ${newTime}`, 'YYYY-MM-DD h:mma', newTimezone);

			}

		// Otherwise, reset the value
		} else {

			newValue = null;

		}

		let result;

		if (value) {

			result = [...value];

			result[1] = newValue;

			// Check if the start datetime needs to be automatically updated
			if (result[1] &&
				(!result[0] || result[0].isAfter(result[1]))
			) {
				result[0] = result[1];
			}

		} else {

			result = [newValue, newValue];

		}

		onChange(result);

	};

	const handleTimezoneChange = (tempValue) => {

		const newTimezone = tempValue;

		// Default to current date
		const newStartDate =
			formValues.startDate ||
			moment().tz(newTimezone).format('YYYY-MM-DD');

		// Default to current time
		const newStartTime =
			formValues.startTime ||
			moment().tz(newTimezone).format('h:mma');

		// Default to current date
		const newEndDate =
			formValues.endDate ||
			moment().tz(newTimezone).format('YYYY-MM-DD');

		// Default to current time
		const newEndTime =
			formValues.endTime ||
			moment().tz(newTimezone).format('h:mma');

		const newStartDatetime =
			moment.tz(`${newStartDate} ${newStartTime}`, 'YYYY-MM-DD h:mma', newTimezone);

		const newEndDatetime =
			moment.tz(`${newEndDate} ${newEndTime}`, 'YYYY-MM-DD h:mma', newTimezone);

		onChange([newStartDatetime, newEndDatetime]);

	};

	return (
		<div
			className={
				`tc-datetime-range-picker
				${isInvalid ? 'is-invalid' : ''}
				${isRequired ? 'is-required' : ''}
			`}>
			<div className="tc-datetime-range-picker-field start-date">
				<Label
					className="sr-only"
					for={startDateLabel}>
					<FormattedMessage {...messages.TC_COMPONENTS_DATETIME_RANGE_PICKER_LABEL_TEXT_START_DATE} />
				</Label>
				<DatePicker
					name={startDateLabel}
					value={formValues.startDate ? moment(formValues.startDate) : null}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_DATETIME_PICKER_LABEL_TEXT_DATE)}
					isInvalid={isInvalid}
					isRequired={isRequired}
					onChange={(newValue) => handleStartChange(startDateLabel, newValue)} />
			</div>
			<div className="tc-datetime-range-picker-field start-time">
				<Label
					className="sr-only"
					for={startTimeLabel}>
					<FormattedMessage {...messages.TC_COMPONENTS_DATETIME_RANGE_PICKER_LABEL_TEXT_START_TIME} />
				</Label>
				<TimePicker
					name={startTimeLabel}
					value={formValues.startTime}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_DATETIME_PICKER_LABEL_TEXT_TIME)}
					isInvalid={isInvalid}
					isRequired={isRequired}
					onChange={(newValue) => handleStartChange(startTimeLabel, newValue)} />
			</div>
			<span className="tc-datetime-range-picker-spacer">
				<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_RANGE_GOES_TO} />
			</span>
			<div className="tc-datetime-range-picker-field end-date">
				<Label
					className="sr-only"
					for={endDateLabel}>
					<FormattedMessage {...messages.TC_COMPONENTS_DATETIME_RANGE_PICKER_LABEL_TEXT_END_DATE} />
				</Label>
				<DatePicker
					name={endDateLabel}
					value={formValues.endDate ? moment(formValues.endDate) : null}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_DATETIME_PICKER_LABEL_TEXT_DATE)}
					isInvalid={isInvalid}
					isRequired={isRequired}
					onChange={(newValue) => handleEndChange(endDateLabel, newValue)} />
			</div>
			<div className="tc-datetime-range-picker-field end-time">
				<Label
					className="sr-only"
					for={endTimeLabel}>
					<FormattedMessage {...messages.TC_COMPONENTS_DATETIME_RANGE_PICKER_LABEL_TEXT_END_TIME} />
				</Label>
				<TimePicker
					name={endTimeLabel}
					value={formValues.endTime}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_DATETIME_PICKER_LABEL_TEXT_TIME)}
					isInvalid={isInvalid}
					isRequired={isRequired}
					onChange={(newValue) => handleEndChange(endTimeLabel, newValue)} />
			</div>
			<div className="tc-datetime-range-picker-field timezone">
				<Label
					className="sr-only"
					for={timezoneLabel}>
					<FormattedMessage {...messages.TC_COMPONENTS_DATETIME_PICKER_LABEL_TEXT_TIMEZONE} />
				</Label>
				<TimezonePicker
					id={timezoneLabel}
					name={timezoneLabel}
					value={formValues.timezone}
					placeholder={intl.formatMessage(
						messages.TC_COMPONENTS_DATETIME_PICKER_LABEL_TEXT_TIMEZONE
					)}
					isInvalid={isInvalid}
					isRequired={isRequired}
					onChange={(newValue) => handleTimezoneChange(newValue)} />
			</div>
		</div>
	);

};

DateTimeRangePicker.propTypes = propTypes;
DateTimeRangePicker.defaultProps = defaultProps;

export default DateTimeRangePicker;
